import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import StoreBadgeLink from '@/components/StoreBadgeLink';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';
import modalBanner from '@/assets/modalBanner.webp';
function CloseIcon(): JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface DownloadAppModalProps {
  open: boolean;
  onClose: () => void;
}

export default function DownloadAppModal({
  open,
  onClose,
}: DownloadAppModalProps): JSX.Element {
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="download-app-modal-title"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: '16px', sm: '24px' },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          backgroundColor: '#F9F8F6',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '500px',
          pt: { xs: '40px', sm: '80px' },
          px: { xs: '28px', sm: '100px' },
          pb: 0,
          outline: 'none',
          overflow: 'hidden',
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            color: color.charcoal['400'],
            '&:hover': { color: color.charcoal.default },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Heading */}
        <Typography
          id="download-app-modal-title"
          sx={{
            ...typography.display,
            color: color.charcoal.default,
            mb: '20px',
            lineHeight: 1.1,
            fontSize: {
              xs: 'var(--font-size-36, 36px)',
              sm: typography.display.fontSize,
            },
          }}
        >
          Take the Vineyard Guide with you
        </Typography>

        {/* Description */}
        <Typography
          sx={{
            ...typography.bodyText,
            color: color.charcoal['500'],
            mb: '24px',
            maxWidth: '420px',
          }}
        >
          Explore the best of UK wine tourism on the go. Download our app for
          offline maps, personalised itineraries, and exclusive offers.
        </Typography>

        {/* Store badges */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: '12px',
            mb: '20px',
          }}
        >
          {/* QR Code - Desktop only */}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <StoreBadgeLink />
          </Box>
          {/* Buttons - Mobile only */}
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              gap: '12px',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
            }}
          >
            <StoreBadgeLink
              platform="apple"
              onClick={(e) => {
                e.stopPropagation();
                gtag('event', 'store_badge_click', {
                  event_category: 'Download',
                  event_label: 'App Store – Download Modal',
                });
              }}
            />
            <StoreBadgeLink
              platform="android"
              onClick={(e) => {
                e.stopPropagation();
                gtag('event', 'store_badge_click', {
                  event_category: 'Download',
                  event_label: 'Google Play – Download Modal',
                });
              }}
            />
          </Box>
        </Box>

        {/* Caption */}
        <Typography
          sx={{
            ...typography.bodySmall,
            color: color.charcoal['300'],
          }}
        >
          Available for iOS and Android devices.
        </Typography>

        {/* Bottom banner */}
        <Box
          component="img"
          src={modalBanner}
          alt=""
          aria-hidden
          loading="lazy"
          sx={{
            display: 'block',
            width: { xs: 'calc(100% + 56px)', sm: 'calc(100% + 200px)' },
            mx: { xs: '-28px', sm: '-100px' },
            mt: '32px',
            pointerEvents: 'none',
          }}
        />
      </Box>
    </Modal>
  );
}
